import React, { useMemo } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../theme';
import { useTranslation } from 'react-i18next';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { convertSpeed, getUnits } from '../pages/WorkoutAnalysisV2';

const WhatIfSummaryBox = ({ chunks, workoutData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const units = getUnits();
  const speedLabel = units === 'imperial' ? 'mph' : 'km/h';

  const { avg_speed, total_duration } = workoutData;

  // Calculate what-if totals from chunks
  const whatIfTotals = useMemo(() => {
    if (!chunks || chunks.length === 0) return null;

    const chunksWithWhatIf = chunks.filter(c => c.what_if);
    if (chunksWithWhatIf.length === 0) return null;

    const totalTime = chunksWithWhatIf.reduce((sum, c) => sum + c.duration, 0);
    const totalTimeDiffPosition = chunksWithWhatIf.reduce((sum, c) => sum + c.what_if.time_diff_position, 0);
    const totalTimeDiffWeight = chunksWithWhatIf.reduce((sum, c) => sum + c.what_if.time_diff_weight, 0);
    const totalTimeDiffTotal = chunksWithWhatIf.reduce((sum, c) => sum + c.what_if.time_diff_total, 0);
    
    const avgSpeedWithPosition = chunksWithWhatIf.reduce((sum, c) => sum + c.what_if.speed_with_position_change * c.duration, 0) / totalTime;
    const avgSpeedWithWeight = chunksWithWhatIf.reduce((sum, c) => sum + c.what_if.speed_with_weight_change * c.duration, 0) / totalTime;
    const avgSpeedWithBoth = chunksWithWhatIf.reduce((sum, c) => sum + c.what_if.speed_with_both_changes * c.duration, 0) / totalTime;

    return {
      timeDiffPosition: totalTimeDiffPosition,
      timeDiffWeight: totalTimeDiffWeight,
      timeDiffTotal: totalTimeDiffTotal,
      avgSpeedWithPosition,
      avgSpeedWithWeight,
      avgSpeedWithBoth
    };
  }, [chunks]);

  const formatTimeDiff = (seconds) => {
    const absSeconds = Math.abs(seconds);
    const minutes = Math.floor(absSeconds / 60);
    const secs = Math.round(absSeconds % 60);
    const sign = seconds < 0 ? '-' : '+';
    return `${sign}${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (!whatIfTotals) {
    return null;
  }

  return (
    <Box sx={{ width: { xs: '100%', sm: '500px' } }}>
      <Paper
        elevation={3}
        sx={{
          p: 2,
          backgroundColor: colors.grey[800],
          borderRadius: "8px",
          height: "100%"
        }}
      >
        <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={1.5}>
          {t('whatIfAnalysis')}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: colors.grey[300], fontSize: '12px', fontWeight: 'bold', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{t('scenario')}</TableCell>
                <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '12px', fontWeight: 'bold', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{t('speed')}</TableCell>
                <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '12px', fontWeight: 'bold', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{t('change')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ color: colors.grey[300], fontSize: '12px', py: 0.5, px: 1, borderBottom: 'none' }}>{t('baseline')}</TableCell>
                <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '12px', py: 0.5, px: 1, borderBottom: 'none' }}>{convertSpeed(avg_speed, units).toFixed(2)} {speedLabel}</TableCell>
                <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '12px', py: 0.5, px: 1, borderBottom: 'none' }}>-</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: colors.grey[300], fontSize: '12px', py: 0.5, px: 1, borderBottom: 'none' }}>{t('positionOnly')}</TableCell>
                <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '12px', py: 0.5, px: 1, borderBottom: 'none' }}>{convertSpeed(whatIfTotals.avgSpeedWithPosition, units).toFixed(2)} {speedLabel}</TableCell>
                <TableCell align="right" sx={{ color: Math.abs(whatIfTotals.timeDiffPosition) < 30 ? colors.blueAccent[400] : whatIfTotals.timeDiffPosition < 0 ? colors.greenAccent[400] : colors.redAccent[400], fontSize: '12px', py: 0.5, px: 1, borderBottom: 'none' }}>
                  {formatTimeDiff(whatIfTotals.timeDiffPosition)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: colors.grey[300], fontSize: '12px', py: 0.5, px: 1, borderBottom: 'none' }}>{t('weightOnly')}</TableCell>
                <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '12px', py: 0.5, px: 1, borderBottom: 'none' }}>{convertSpeed(whatIfTotals.avgSpeedWithWeight, units).toFixed(2)} {speedLabel}</TableCell>
                <TableCell align="right" sx={{ color: Math.abs(whatIfTotals.timeDiffWeight) < 30 ? colors.blueAccent[400] : whatIfTotals.timeDiffWeight < 0 ? colors.greenAccent[400] : colors.redAccent[400], fontSize: '12px', py: 0.5, px: 1, borderBottom: 'none' }}>
                  {formatTimeDiff(whatIfTotals.timeDiffWeight)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: colors.grey[300], fontSize: '12px', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{t('bothChanges')}</TableCell>
                <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '12px', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{convertSpeed(whatIfTotals.avgSpeedWithBoth, units).toFixed(2)} {speedLabel}</TableCell>
                <TableCell align="right" sx={{ color: Math.abs(whatIfTotals.timeDiffTotal) < 30 ? colors.blueAccent[400] : whatIfTotals.timeDiffTotal < 0 ? colors.greenAccent[400] : colors.redAccent[400], fontSize: '12px', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>
                  {formatTimeDiff(whatIfTotals.timeDiffTotal)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Box mt={1.5} pt={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="h5" color={colors.grey[300]} fontWeight="bold">
              {t('newDuration')}:
            </Typography>
            <Typography variant="h3" color={colors.blueAccent[400]} fontWeight="bold">
              {Math.floor((total_duration + whatIfTotals.timeDiffTotal) / 3600)}h {Math.floor(((total_duration + whatIfTotals.timeDiffTotal) % 3600) / 60)}m
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color={colors.grey[300]} fontWeight="bold">
              {t('totalTimeDifference')}:
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              {Math.abs(whatIfTotals.timeDiffTotal) < 30 ? null :
               whatIfTotals.timeDiffTotal < 0 ? 
                <TrendingDownIcon sx={{ fontSize: "20px", color: colors.greenAccent[500] }} /> : 
                <TrendingUpIcon sx={{ fontSize: "20px", color: colors.redAccent[500] }} />
              }
              <Typography 
                variant="h6" 
                color={Math.abs(whatIfTotals.timeDiffTotal) < 30 ? colors.blueAccent[400] : whatIfTotals.timeDiffTotal < 0 ? colors.greenAccent[400] : colors.redAccent[400]}
                fontWeight="bold"
              >
                {formatTimeDiff(whatIfTotals.timeDiffTotal)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default WhatIfSummaryBox;
